<template>
  <article
    class="widget-lottery-card"
    :class="{
      'widget-lottery-card--alternate': cardDecor.alternate,
    }"
  >
    <div
      class="widget-lottery-card__decor"
      aria-hidden="true"
    >
      <span
        class="widget-lottery-card__ball widget-lottery-card__ball--hero"
      >
        {{ cardDecor.ballDigit }}
      </span>
    </div>

    <div class="widget-lottery-card__hero">
      <div class="widget-lottery-card__hero-content">
        <div
          class="widget-lottery-card__identity"
        >
          <div class="widget-lottery-card__flag-wrap">
            <img
              :src="flagUrl"
              :alt="lottery.country || lottery.game_name"
              class="widget-lottery-card__flag"
              loading="lazy"
            >
          </div>
          <div class="widget-lottery-card__names">
            <template v-if="displayLines.format">
              <p
                class="widget-lottery-card__title-line"
                :title="lottery.game_name"
              >
                {{ displayLines.title }}
              </p>
              <p class="widget-lottery-card__format-line">
                {{ displayLines.format }}
              </p>
            </template>
            <h3
              v-else
              class="widget-lottery-card__title widget-lottery-card__title--clamp"
              :title="lottery.game_name"
            >
              {{ displayLines.title }}
            </h3>
          </div>
          <span
            class="widget-lottery-card__badge"
            aria-hidden="true"
          >{{ cardDecor.badgeDigit }}</span>
        </div>
      </div>
    </div>

    <div class="widget-lottery-card__footer">
      <p class="widget-lottery-card__countdown-label">
        {{ $t('widget.nextDrawStartsIn') }}
      </p>

      <div
        class="widget-lottery-card__timer"
        aria-live="polite"
      >
        <span class="widget-lottery-card__unit">{{ timeParts.hours }}h</span>
        <span class="widget-lottery-card__sep">:</span>
        <span class="widget-lottery-card__unit">{{ timeParts.minutes }}m</span>
        <span class="widget-lottery-card__sep">:</span>
        <span class="widget-lottery-card__unit">{{ timeParts.seconds }}s</span>
      </div>

      <button
        v-if="!betClosed"
        type="button"
        class="widget-lottery-card__bet"
        @click="handleBet"
      >
        {{ $t('betslip.betNow') }}
      </button>
      <button
        v-else
        type="button"
        class="widget-lottery-card__bet"
        disabled
      >
        {{ $t('betslip.betClosed') }}
      </button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { UpcomingDraw } from '@/types/lottery'
import { getGameLogoUrl } from '@/utils/resultParser'
import {
  getWidgetCardDecor,
  getWidgetGameDisplayLines,
} from '@/utils/widgetCard'
import { iframeCommunication } from '@/utils/iframeCommunication'

const props = defineProps<{
  lottery: UpcomingDraw
}>()

const emit = defineEmits<{
  (e: 'remove', lottery: UpcomingDraw): void
}>()

const route = useRoute()
const router = useRouter()

const betClosed = ref(false)
const countdown = ref({ hours: 0, minutes: 0, seconds: 0 })

const flagUrl = computed(() =>
  getGameLogoUrl(props.lottery.game_logo, props.lottery.game_slug)
)

const displayLines = computed(() =>
  getWidgetGameDisplayLines(props.lottery.game_name)
)

const cardDecor = computed(() =>
  getWidgetCardDecor(props.lottery.game_slug || props.lottery.game_name)
)

const timeParts = computed(() => ({
  hours: String(countdown.value.hours).padStart(2, '0'),
  minutes: String(countdown.value.minutes).padStart(2, '0'),
  seconds: String(countdown.value.seconds).padStart(2, '0'),
}))

const updateCountdown = () => {
  if (!props.lottery?.cutoff) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }

  const cutoffRaw = props.lottery.cutoff as string | Date
  const cutoffTime = typeof cutoffRaw === 'string'
    ? new Date(cutoffRaw.replace(' ', 'T'))
    : new Date(cutoffRaw)
  const diff = cutoffTime.getTime() - Date.now()

  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }

  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  }
}

let timer: number | undefined

onMounted(() => {
  updateCountdown()
  timer = window.setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  if (timer) {
    window.clearInterval(timer)
  }
})

watch(
  () => props.lottery.cutoff,
  () => {
    updateCountdown()
  }
)

watch(
  () => [countdown.value.hours, countdown.value.minutes, countdown.value.seconds],
  ([hours, minutes, seconds]) => {
    if (hours === 0 && minutes === 0 && seconds === 0 && !betClosed.value) {
      betClosed.value = true
      window.setTimeout(() => {
        emit('remove', props.lottery)
      }, 3000)
    }
  }
)

const handleBet = () => {
  const payload = {
    gameSlug: props.lottery.game_slug,
    drawId: props.lottery.id,
    gameName: props.lottery.game_name,
    cutoff: props.lottery.cutoff,
    drawDate: props.lottery.draw_date,
  }

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendMessage('widget-bet', payload)
    return
  }

  router.push({
    path: `/lottery/${props.lottery.game_slug}`,
    query: { ...route.query, draw: String(props.lottery.id) },
  })
}
</script>
